import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core'
import { Customer } from 'depoto-core/src/entities'
import { CoreService } from '../../services'
import { OrderDetailService } from '../../services/order-detail.service'
import { Router } from '@angular/router'
import { copyAddressForOrder, SchemaUtil } from '../../utils'

const schema = {
  id: null,
  companyName: null,
  firstName: null,
  lastName: null,
  name: null,
  email: null,
  priceLevel: {
    id: null,
    name: null,
    isPercentage: null,
    percent: null,
    withVat: null,
    currency: {
      id: null,
      name: null,
      ratio: null,
    },
    externalId: null,
  },
}

@Component({
  selector: 'customer-search',
  templateUrl: './customer-search.component.html',
  styleUrls: ['./customer-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CustomerSearchComponent implements OnInit {
  @Input() isInModal = false
  @Input() isOnOrdersPage = false
  @Output() customerChange: EventEmitter<any> = new EventEmitter()
  public searchPhrase = ''
  public customers: Customer[] = []
  public hasRoleSalesman: boolean
  public searchInAllCustomers = true
  currentPage: number
  public maxPageNumber = 999
  public isLoading = false
  private currentUserId

  constructor(
    private core: CoreService,
    public orderDetailService: OrderDetailService,
    private router: Router
  ) {}

  async ngOnInit() {
    this.hasRoleSalesman = this.core.services.user.hasRole('ROLE_SALESMAN')
    if (!this.isInModal) {
      await this.searchForCustomer(this.currentPage)
    }

    setTimeout(() => {
      const el = document.getElementById('customer-search-btn-next')
      if (!this.isInModal && el) {
        const observer = new IntersectionObserver(
          entries => {
            if (entries[0].isIntersecting === true && this.customers.length > 19) {
              this.searchForCustomer(this.currentPage)
            }
          },
          { threshold: [0] }
        )
        observer.observe(el)
      }
    }, 300)
  }

  public async searchForCustomer(pageNo = 1) {
    this.currentPage = pageNo
    this.isLoading = true
    let customers: any[]
    if (this.searchInAllCustomers) {
      customers = await this.core.services.customer
        .getList({ page: this.currentPage, filters: { fulltext: this.searchPhrase } }, schema)
        .then(result => {
          this.maxPageNumber = result.paginator.endPage
          return result.items
        })
    } else {
      if (!this.currentUserId) {
        this.currentUserId = this.core.services.user.user.id
      }
      customers = await this.core.services.customer
        .getList({ page: this.currentPage, filters: { fulltext: this.searchPhrase, user: this.currentUserId } }, schema)
        .then(result => {
          this.maxPageNumber = result.paginator.endPage
          return result.items
        })
    }
    if (this.currentPage === 1) {
      this.customers = customers
    } else {
      this.customers = [...this.customers, ...customers]
    }
    this.currentPage++
    this.isLoading = false
  }

  public async selectCustomer(customer: Customer) {
    if (!this.isOnOrdersPage) {
      try {
        await this.core.services.customer
          .getById(customer.id, { id: null, addresses: SchemaUtil.address })
          .then(result => {
            this.orderDetailService.isEditedAddress = true
            customer.addresses = result.addresses
            const storedBillingAddresses = customer.addresses.filter(a => !!a.isBilling && !!a.isStored)
            if (storedBillingAddresses.length === 1) {
              this.orderDetailService.order.invoiceAddress = copyAddressForOrder(storedBillingAddresses[0], true)
            }
          })
      } catch (err) {
        console.error(err)
      }
      this.orderDetailService.order.customer = customer
    }
    if (this.isInModal) {
      this.customerChange.emit(customer)
    } else {
      if (
        customer.users &&
        customer.users.length &&
        customer.users[0].checkouts &&
        customer.users[0].checkouts.length
      ) {
        this.orderDetailService.order.checkout = customer.users[0].checkouts[0]
      }
      this.orderDetailService.activeLink = 'address'
      this.router.navigate(['/order/address'])
    }
  }

  public trackById(index: number, value: Customer): number {
    return value.id
  }

  getTagNames(customer: Customer): string {
    return customer.tags && customer.tags.length ? customer.tags.map(t => t.name).join(', ') : ''
  }
}
